import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import Button from '@components/button/Button';
import Chip from '@components/chip/Chip';
import Icon from '@components/icon/Icon';
import { deriveDarkColor } from '@features/lead-management/utils/common';

// Generic interfaces
export interface SearchableItem {
  id: string;
  name: string;
  color?: string;
}

export interface SearchableDropdownProps<T extends SearchableItem> {
  value: string;
  onChange: (value: string) => void;
  onItemsChange: (items: T[], type: 'add' | 'remove') => void;
  selectedItems: T[];
  availableItems?: T[];
  onUpdateItem?: (item: T) => void;
  onCreateItem?: (name: string, color?: string) => T;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowCreate?: boolean;
  allowMultiple?: boolean;
  searchIcon?: React.ReactNode;
  hideChevron?: boolean;
  renderChip?: (
    item: T,
    onRemove: (evt: React.MouseEvent) => void
  ) => React.ReactNode;
  chipPosition?: 'top' | 'bottom';
  SelectorComponent?: React.ComponentType<any>;
  validateNameLength?: (name: string) => boolean;
  renderSelectedBelow?: boolean;
  selectorProps?: Record<string, any>;
  defaultTagColor?: string;
  showSelectionTooltip?: boolean;
}

const SearchableDropdown = <T extends SearchableItem>({
  value,
  onChange,
  onItemsChange,
  selectedItems,
  availableItems = [],
  onUpdateItem,
  onCreateItem,
  placeholder = 'Search or select...',
  className = '',
  disabled = false,
  allowCreate = true,
  allowMultiple = true,
  renderChip,
  chipPosition = 'bottom',
  SelectorComponent,
  validateNameLength,
  searchIcon,
  hideChevron,
  renderSelectedBelow = false,
  selectorProps,
  defaultTagColor = '#F2E1E9',
  showSelectionTooltip = false
}: SearchableDropdownProps<T>) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(defaultTagColor);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
  };

  const handleInputClick = () => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    onChange(next);
    setIsDropdownOpen(next.trim().length > 0);
  };

  useEffect(() => {
    setIsDropdownOpen(value.trim().length > 0);
  }, [value]);

  // Close dropdown on scroll/resize
  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const close = () => setIsDropdownOpen(false);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('resize', close);
    };
  }, [isDropdownOpen]);

  const handleItemSelect = (item: T) => {
    if (allowMultiple) {
      if (!selectedItems.some((selected) => selected.id === item.id)) {
        onItemsChange([...selectedItems, item], 'add');
      } else {
        onItemsChange(selectedItems, 'add');
      }
    } else {
      onItemsChange([item], 'add');
    }
    setIsDropdownOpen(false);
  };

  const handleCreateItem = (itemName: string, color?: string) => {
    if (!onCreateItem) return;

    if (validateNameLength && !validateNameLength(itemName)) {
      return;
    }

    const newItem = onCreateItem(itemName, color);

    if (allowMultiple) {
      if (
        !selectedItems.some(
          (selected) => selected.name.toLowerCase() === itemName.toLowerCase()
        )
      ) {
        onItemsChange([...selectedItems, newItem], 'add');
      }
    } else {
      onItemsChange([newItem], 'add');
      setIsDropdownOpen(false);
    }

    onChange('');
    setIsDropdownOpen(false);
  };

  const handleUpdateItem = (newItem: T) => {
    if (newItem.id && onUpdateItem) {
      onUpdateItem(newItem);
    }
  };

  const handleRemoveItem = (itemToRemove: T, evt: React.MouseEvent) => {
    evt.stopPropagation();
    onItemsChange(
      selectedItems.filter((item) => item.id !== itemToRemove.id),
      'remove'
    );
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value.trim()) {
      e.preventDefault();

      // If there's a search value and allowCreate is true, create a new item
      if (allowCreate && onCreateItem) {
        // Use the selected color for Enter key creation
        handleCreateItem(value.trim(), selectedColor);
      }
    }
  };

  const defaultRenderChip = (
    item: T,
    onRemove: (e: React.MouseEvent) => void
  ) => (
    <Chip
      key={item.id}
      label={item.name}
      className="px-2 py-1 text-sm"
      style={{
        backgroundColor: item.color || '#FFFFFF',
        color: deriveDarkColor(item.color || '#FFFFFF')
      }}
      labelStyle="!text-sm !font-normal"
      size="16"
      onRemove={onRemove}
    />
  );

  const validSelectedItems = selectedItems.filter((item) => item.id);

  const showChip =
    allowMultiple && renderSelectedBelow && validSelectedItems.length > 0;

  const getInputComponent = () => {
    if (allowMultiple) {
      return (
        <div
          className={clsx(
            'flex flex-1 items-center gap-2',
            !renderSelectedBelow && 'flex-wrap'
          )}
        >
          {searchIcon}
          {!renderSelectedBelow &&
            validSelectedItems.map((item) =>
              renderChip
                ? renderChip(item, (evt) => handleRemoveItem(item, evt))
                : defaultRenderChip(item, (evt) => handleRemoveItem(item, evt))
            )}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="min-w-30 flex-1 bg-transparent text-sm text-darkGrey outline-none placeholder:!text-base placeholder:!font-normal placeholder:text-grey"
            readOnly={false}
          />
        </div>
      );
    }

    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="min-w-30 flex-1 bg-transparent text-sm text-darkGrey outline-none placeholder:!text-base placeholder:!font-normal placeholder:text-grey"
        readOnly={false}
      />
    );
  };

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {/* Selected tags above input (scoped via prop) */}
      {chipPosition === 'top' && showChip && (
        <div className="m-2 flex flex-wrap items-center gap-2">
          {validSelectedItems.map((item) =>
            renderChip
              ? renderChip(item, (evt) => handleRemoveItem(item, evt))
              : defaultRenderChip(item, (evt) => handleRemoveItem(item, evt))
          )}
        </div>
      )}

      {/* Input Container */}
      <Button
        variant="tertiary"
        handleClick={handleInputClick}
        size="medium"
        className={clsx(
          '!h-auto !max-h-fit w-full min-w-166 justify-between border border-grey40 !bg-theme',
          'flex min-h-12 cursor-pointer items-center gap-2',
          'px-4 py-3',
          'focus-within:border-primary',
          disabled && 'cursor-not-allowed opacity-50'
        )}
        disabled={disabled}
      >
        {getInputComponent()}
        {!hideChevron && (
          <Icon
            name="down"
            className={clsx(
              'size-4 text-grey transition-transform',
              isDropdownOpen && 'rotate-180'
            )}
          />
        )}
      </Button>

      {/* Selected tags below input (scoped via prop) */}
      {chipPosition === 'bottom' && showChip && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {validSelectedItems.map((item) =>
            renderChip
              ? renderChip(item, (evt) => handleRemoveItem(item, evt))
              : defaultRenderChip(item, (evt) => handleRemoveItem(item, evt))
          )}
        </div>
      )}

      {/* Render the selector component if provided */}
      {SelectorComponent && (
        <SelectorComponent
          isOpen={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
          onSelectItem={handleItemSelect}
          onCreateItem={allowCreate ? handleCreateItem : undefined}
          onUpdateItem={handleUpdateItem}
          selectedItems={selectedItems}
          searchValue={value}
          itemOptions={availableItems}
          className={selectorProps?.positionFixed ? undefined : 'top-full'}
          selectedColor={selectedColor}
          onColorChange={handleColorChange}
          positionFixed={selectorProps?.positionFixed}
          getAnchorRect={() => {
            const root = containerRef.current;
            if (!root) return null;
            const buttonEl = root.querySelector('button');
            if (buttonEl && buttonEl instanceof HTMLElement) {
              return buttonEl.getBoundingClientRect();
            }
            return root.getBoundingClientRect();
          }}
          showSelectionTooltip={showSelectionTooltip}
          {...selectorProps}
        />
      )}
    </div>
  );
};

export default SearchableDropdown;
